import { METADATA_KEY } from "../../constants";
import type { RydeUiUnit } from "../../models";
import type { IAuditLogIdCollectionConfig } from "./audit-log.models";

/**
 * The diff format update rows are written in from this version on (metadata.diffVersion): removed jsonb keys are
 * null, an array of items with ids holds one entry per item (the id plus the changed fields, a new item in full, a
 * removed item left out) and any other changed array is stored whole. Rows without it patch arrays by index.
 */
export const AUDIT_LOG_DIFF_VERSION = 3;

/** Entities whose metadata holds area ids (areaIds, innerAreaIds), the only histories that need area names. */
export const AUDIT_LOG_AREA_NAME_ENTITIES = new Set<RydeUiUnit>([
  "ride",
  "course",
]);

/** Joins the names of the items that came into or went out of a collection. */
export const AUDIT_LOG_ITEMS_LIST_SEPARATOR = ", ";

export const AUDIT_LOG_NUMERIC_KEY_REGEX = /^\d+$/;
export const AUDIT_LOG_ID_LIST_KEY_SUFFIX = "Ids";

export const AUDIT_LOG_ID_COLLECTIONS: Record<
  string,
  IAuditLogIdCollectionConfig
> = {
  [`${METADATA_KEY}.passengerIds`]: {
    isOrdered: false,
    labelSource: "passengers",
  },
  [`${METADATA_KEY}.unitIds`]: { isOrdered: false, labelSource: "units" },
  [`${METADATA_KEY}.areaIds`]: { isOrdered: false, labelSource: "areas" },
  [`${METADATA_KEY}.innerAreaIds`]: { isOrdered: false, labelSource: "areas" },
  [`${METADATA_KEY}.stationIds`]: { isOrdered: true, labelSource: "stations" },
  [`${METADATA_KEY}.pickupStationsIds`]: {
    isOrdered: true,
    labelSource: "stations",
  },
  [`${METADATA_KEY}.dropoffStationsIds`]: {
    isOrdered: true,
    labelSource: "stations",
  },
};

export const AUDIT_LOG_ORDERED_ITEM_COLLECTIONS = new Set([
  `${METADATA_KEY}.stationsInfo`,
]);

export const AUDIT_LOG_SYSTEM_SERVICE_NAMES = new Set([
  "Api",
  "Syncer",
  "EtlRunner",
  "EtlScheduler",
  "Integration",
  "Messaging",
  "DriverApi",
  "PassengerApi",
  "LandingsApi",
  "AuditLog",
  "Unset",
]);

export const AUDIT_LOG_SERVICE_DISPLAY_NAMES: Record<string, string> = {
  Api: "System",
  Syncer: "Syncer",
  EtlRunner: "ETL",
  EtlScheduler: "ETL Scheduler",
  Integration: "Integration",
  Messaging: "Messaging",
  DriverApi: "Driver API",
  PassengerApi: "Passenger API",
  LandingsApi: "Landings API",
  AuditLog: "Audit Log",
  Unset: "System",
};

export const AUDIT_LOG_CSV_INDENT_INCREMENT = 2;

export const AUDIT_LOG_MAX_DELTA_FRACTION_DIGITS = 2;
export const AUDIT_LOG_PERCENT_FACTOR = 100;
export const AUDIT_LOG_NUMBER_LIST_SEPARATOR = ",";

/** Which kind of change the history filter keeps. */
export const AuditLogChangeFilters = {
  added: "added",
  removed: "removed",
  changed: "changed",
  reordered: "reordered",
} as const;

export type TAuditLogChangeFilter =
  (typeof AuditLogChangeFilters)[keyof typeof AuditLogChangeFilters];

/** What the history export takes: every loaded record, or only those the search and filters leave. */
export const AuditLogExportScopes = {
  all: "all",
  filtered: "filtered",
} as const;

export type TAuditLogExportScope =
  (typeof AuditLogExportScopes)[keyof typeof AuditLogExportScopes];

export const AUDIT_LOG_CHANGE_FILTER_LIST: TAuditLogChangeFilter[] =
  Object.values(AuditLogChangeFilters);

/** Technical fields: kept in the history, drawn in grey so business fields stand out. */
export const AUDIT_LOG_MUTED_KEYS = new Set([
  "diffVersion",
  "isManualPolylineEdit",
  "routePolyline",
  "oldId",
  "externalId",
]);

/** Numeric fields where the size of a change (+288 / -64) matters more than the two values. */
export const AUDIT_LOG_NUMERIC_DELTA_KEYS = new Set([
  "distances",
  "durations",
  "totalDistance",
  "totalDuration",
  "boardingSegmentDuration",
]);
